import { useContext, useState } from 'react';
import * as S from './style'
import * as T from 'react-toastify';
import { Grid, Radio, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Formik, Form, useFormik } from 'formik';
import * as Yup from "yup";
import "assets/theme/custom-flatpickr.css";
import 'moment/locale/pt-br';
import showToast from 'components/Toast/Toast';
import { TokenContext } from "context/TokenContext";
import { requestCommunityDify } from 'utils/requests/dify';
import { useNavigate } from 'react-router-dom';
import { FaInstagram, FaTelegram, FaDiscord, FaTiktok, FaWhatsapp, FaEllipsisH } from 'react-icons/fa';
import DashboardLayout from 'defaultComponents/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'defaultComponents/Navbars/DashboardNavbar';
import zeCommunityImage from 'assets/ze_community.png';
import { createCommunity } from 'utils/requests/communities';

const BuildCommunityForm = ({ initialData }) => {
  const { decodedToken } = useContext(TokenContext);
  const role = decodedToken?.type;

  const navigate = useNavigate();
  
  const [validateFields, setValidateFields] = useState(false);

  const platforms = [
    { id: 'instagram', label: 'Instagram', icon: 'instagram' },
    { id: 'tiktok', label: 'TikTok', icon: 'tiktok' },
    { id: 'telegram', label: 'Telegram', icon: 'telegram' },
    { id: 'discord', label: 'Discord', icon: 'discord' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp' },
    { id: 'other', label: 'Outro', icon: 'other' }
  ];

  const contentTypes = [
    { id: 'curso', label: 'Curso' },
    { id: 'mentoria', label: 'Mentoria' },
    { id: 'desafio', label: 'Desafio' },
    { id: 'outro', label: 'Outros' }
  ];

  const communicationTypes = [
    { id: 'texto', label: 'Texto' },
    { id: 'video', label: 'Vídeo' },
    { id: 'audio', label: 'Áudio' },
    { id: 'live', label: 'Live' },
    { id: 'enquete', label: 'Enquete' }
  ];

  const validation = useFormik({
    validateOnChange: validateFields,
    validateOnBlur: false,
    enableReinitialize: true,

    initialValues: initialData?.planDetails || initialData || {
      purpose: '',
      specialInfo: '',
      subscription: 'gratuita',
      platform: [],
      content: '',
      contentTypes: [],
      communicationTypes: [],
    },
    validationSchema: Yup.object({
      purpose: Yup.string().required('O propósito da comunidade é obrigatório'),
      specialInfo: Yup.string().required('Este campo é obrigatório'),
      subscription: Yup.string().required('Este campo é obrigatório'),
      platform: Yup.array().min(1, 'Selecione pelo menos uma plataforma'),
      content: Yup.string(),
      contentTypes: Yup.array().min(1, 'Selecione pelo menos um tipo de conteúdo'),
      communicationTypes: Yup.array().min(1, 'Selecione pelo menos um tipo de comunicação'),
    }),
    onSubmit: async (values) => {
      try {
        const chatbotResponse = await requestCommunityDify(values);
        if (!chatbotResponse?.answer) {
          const errorMsg = chatbotResponse?.rawMessage?.[0]?.split(': ')[1] || chatbotResponse?.message;
          throw new Error(errorMsg || "Resposta do chatbot inválida");
        }

        const payload = {
          user_related: decodedToken?.id,
          plan_details: JSON.stringify({
            ...(chatbotResponse.answer || {}),
            purpose: values.purpose,
            specialInfo: values.specialInfo, 
            subscription: values.subscription,
            platform: values.platform,
            content: values.content,
            contentTypes: values.contentTypes,
            communicationTypes: values.communicationTypes,
          })
        };

        const createdCommunity = await createCommunity(payload);

        showToast("Comunidade criada com sucesso!", "success");
        navigate(`/comunidade/agente/${createdCommunity.id}`);
      } catch (error) {
        const errorMsg = error?.rawMessage?.[0]?.replace(/^Error:\\s*/, '') || error?.message;
        showToast(
          errorMsg
            ? `Erro ao criar comunidade: ${errorMsg}`
            : "Erro ao criar comunidade.",
          "error"
        );
      }
    }
  });

  const handlePlatformChange = (platformId) => {
    const currentPlatforms = [...validation.values.platform];
    const index = currentPlatforms.indexOf(platformId);
    
    if (index === -1) {
      currentPlatforms.push(platformId);
    } else {
      currentPlatforms.splice(index, 1);
    }
    
    validation.setFieldValue('platform', currentPlatforms);
  };

  const handleContentTypeChange = (typeId) => {
    const currentTypes = [...validation.values.contentTypes];
    const index = currentTypes.indexOf(typeId);
    
    if (index === -1) {
      currentTypes.push(typeId);
    } else {
      currentTypes.splice(index, 1);
    }
    
    validation.setFieldValue('contentTypes', currentTypes);
  };

  const handleCommunicationTypeChange = (typeId) => {
    const currentTypes = [...validation.values.communicationTypes];
    const index = currentTypes.indexOf(typeId);
    
    if (index === -1) {
      currentTypes.push(typeId);
    } else {
      currentTypes.splice(index, 1);
    }
    
    validation.setFieldValue('communicationTypes', currentTypes);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <T.ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Grid container spacing={3}>
        <Grid item md={2} xl={2} sx={{ minHeight: "200px", maxHeight: "200px" }}>
          <S.ZeCommunityImage sx={{ borderRadius: "5px" }}>
            <img src={zeCommunityImage} alt="Zé Community" />
          </S.ZeCommunityImage>
        </Grid>
        <Grid item md={10} xl={10} sx={{ maxHeight: "900px"}}>
          <S.ProjectCard>
            <S.FormHeaderContainer>
              <Grid container justifyContent='space-between'>
                <S.FormTitleGrid container flexDirection='column' item md={7}>
                  <S.FormTitleText>Vamos construir sua comunidade?</S.FormTitleText>
                  <S.FormSubTitle>Responda essas perguntas e eu montarei tudo para você!</S.FormSubTitle>
                </S.FormTitleGrid>
                <S.FormHeaderBadgeContainer item md={5}>
                  <S.BadgeGrid item>
                    <p>Geral</p>
                  </S.BadgeGrid>
                </S.FormHeaderBadgeContainer>
              </Grid>
            </S.FormHeaderContainer>
            <Formik 
              initialValues={validation.initialValues} 
              validationSchema={validation.validationSchema} 
              onSubmit={validation.handleSubmit}
            >
              <Form>
                <S.FormGrid container flexDirection='column' gap='30px'>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Qual é o propósito central da sua comunidade?</S.SectionTitle>
                      <S.InputContainer item md={12} xl={12}>
                        <S.InputGrid>
                          <S.Input
                            multiline
                            placeholder='Ex: Criar um espaço de apoio entre mães empreendedoras'
                            name='purpose'
                            id='purpose'
                            value={validation.values.purpose}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            error={validation.touched.purpose && Boolean(validation.errors.purpose)}
                            helperText={validation.touched.purpose && validation.errors.purpose}
                          />
                        </S.InputGrid>
                      </S.InputContainer>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>O que tem de especial em você, no seu projeto ou na sua audiência que seu agente deveria saber?</S.SectionTitle>
                      <S.InputContainer item md={12} xl={12}>
                        <S.InputGrid>
                          <S.Input
                            multiline
                            placeholder='Ex: Uma causa que te inspira, algo único sobre sua trajetória ou o tipo de conexão que você quer construir com as pessoas.'
                            name='specialInfo'
                            id='specialInfo'
                            value={validation.values.specialInfo}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            error={validation.touched.specialInfo && Boolean(validation.errors.specialInfo)}
                            helperText={validation.touched.specialInfo && validation.errors.specialInfo}
                          />
                        </S.InputGrid>
                      </S.InputContainer>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Onde você pretende criar sua comunidade?</S.SectionTitle>
                      <S.FormSectionTitle>(Escolha uma ou mais plataformas onde você vai se sentir à vontade para se conectar com o seu público)</S.FormSectionTitle>
                      <S.PlatformContainer container spacing={2}>
                        {platforms.map((platform) => (
                          <Grid item key={platform.id}>
                            <S.PlatformButton
                              onClick={() => handlePlatformChange(platform.id)}
                              className={validation.values.platform.includes(platform.id) ? 'selected' : ''}
                            >
                              {platform.icon === 'instagram' && <FaInstagram size={28} />}
                              {platform.icon === 'whatsapp' && <FaWhatsapp size={28} />}
                              {platform.icon === 'telegram' && <FaTelegram size={28} />}
                              {platform.icon === 'discord' && <FaDiscord size={28} />}
                              {platform.icon === 'tiktok' && <FaTiktok size={28} />}
                              {platform.icon === 'other' && <FaEllipsisH size={28} />}
                            </S.PlatformButton>
                            <S.PlatformLabel>{platform.label}</S.PlatformLabel>
                          </Grid>
                        ))}
                      </S.PlatformContainer>
                      {validation.touched.platform && validation.errors.platform && (
                        <S.FormErrorText>{validation.errors.platform}</S.FormErrorText>
                      )}
                    </Grid>            
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Sua comunidade será gratuita ou com assinatura?</S.SectionTitle>
                      <S.FormRadioGroup
                        name="subscription"
                        value={validation.values.subscription}
                        onChange={validation.handleChange}
                        row
                      >
                        <FormControlLabel value="gratuita" control={<Radio />} label="Gratuita" />
                        <FormControlLabel value="assinatura" control={<Radio />} label="Assinatura mensal" />
                        <FormControlLabel value="naosei" control={<Radio />} label="Ainda não sei" />
                      </S.FormRadioGroup>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Você já tem algum conteúdo ou produto para compartilhar na comunidade?</S.SectionTitle>
                      <S.InputContainer item md={12} xl={12}>
                        <S.InputGrid>
                          <S.Input
                            multiline
                            placeholder='Ex: E-book, curso, produto digital, linha de roupas, lista de espera, etc.'
                            name='content'
                            id='content'
                            value={validation.values.content}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                        </S.InputGrid>
                      </S.InputContainer>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Tem algo especial que você pretende lançar dentro da comunidade?</S.SectionTitle>
                      <Grid container spacing={2}>
                        {contentTypes.map((type) => (
                          <Grid item key={type.id}>
                            <S.FormCheckboxLabel
                              control={
                                <Checkbox 
                                  checked={validation.values.contentTypes.includes(type.id)}
                                  onChange={() => handleContentTypeChange(type.id)}
                                  name={`contentType-${type.id}`}
                                />
                              }
                              label={type.label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {validation.touched.contentTypes && validation.errors.contentTypes && (
                        <S.FormErrorText>{validation.errors.contentTypes}</S.FormErrorText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={12} xl={12}>
                      <S.SectionTitle>Como você costuma se comunicar melhor com seu público?</S.SectionTitle>
                      <Grid container spacing={2}>
                        {communicationTypes.map((type) => (
                          <Grid item key={type.id}>
                            <S.FormCheckboxLabel
                              control={
                                <Checkbox 
                                  checked={validation.values.communicationTypes.includes(type.id)}
                                  onChange={() => handleCommunicationTypeChange(type.id)}
                                  name={`communicationType-${type.id}`}
                                />
                              }
                              label={type.label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {validation.touched.communicationTypes && validation.errors.communicationTypes && (
                        <S.FormErrorText>{validation.errors.communicationTypes}</S.FormErrorText>
                      )}
                    </Grid>
                  </Grid>
                  <S.FormButtonContainer container spacing={2}>
                    <Grid item>
                      <S.FormSubmitButton 
                        type="submit"
                        disabled={validation.isSubmitting}
                        isSubmitting={validation.isSubmitting}
                        onClick={() => setValidateFields(true)}
                      >
                        {validation.isSubmitting && <CircularProgress size={20} style={{ marginRight: '10px' }} color="inherit" />}
                        {validation.isSubmitting 
                          ? 'Criando comunidade...' 
                          : Object.keys(initialData || {}).length > 0 
                            ? 'Atualizar plano da comunidade' 
                            : 'Criar plano de comunidade'}
                      </S.FormSubmitButton>
                    </Grid>
                    {Object.keys(initialData || {}).length > 0 && (
                      <Grid item>
                        <S.FormCancelButton
                          variant="outlined"
                          onClick={() => navigate('/comunidade/agente', { state: { initialTab: 'general' } })}
                        >
                          <Close />
                          Manter dados atuais
                        </S.FormCancelButton>
                      </Grid>
                    )}
                  </S.FormButtonContainer>
                </S.FormGrid>
              </Form>
            </Formik>
          </S.ProjectCard>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default BuildCommunityForm;